/**
 * Display helpers.
 *
 * Money arrives as integer paise (see the MONEY note in schema.prisma) and is
 * grouped Indian-style — 1,00,00,000, not 10,000,000 — because that is what
 * every other number an Indian operator reads looks like.
 */

export function inr(paise: number | null | undefined, opts: { paise?: boolean } = {}): string {
  if (paise === null || paise === undefined) return '—';
  const negative = paise < 0;
  const abs = Math.abs(paise);
  const whole = Math.floor(abs / 100);
  const fraction = abs % 100;

  const s = String(whole);
  let grouped = s;
  if (s.length > 3) {
    const last3 = s.slice(-3);
    const rest = s.slice(0, -3);
    grouped = `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
  }
  const suffix = opts.paise && fraction ? `.${String(fraction).padStart(2, '0')}` : '';
  return `${negative ? '-' : ''}₹${grouped}${suffix}`;
}

/** A listing price arrives as a decimal string, not paise. */
export function rupees(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return inr(Math.round(n * 100));
}

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN').format(value);
}

export function date(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const then = new Date(value).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'minute'],
    [3600, 'hour'],
    [86400, 'day'],
    [604800, 'week'],
    [2592000, 'month'],
    [31536000, 'year'],
  ];
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  let previous = 1;
  for (const [limit, unit] of units) {
    if (seconds < limit * (unit === 'minute' ? 60 : 1) || limit === 31536000) {
      const divisor = unit === 'minute' ? 60 : previous;
      if (seconds < limit || limit === 31536000) return rtf.format(-Math.round(seconds / divisor), unit);
    }
    previous = limit;
  }
  return date(value);
}

/**
 * SELLER → Seller, JOB_AVAILABLE → Job available.
 *
 * Acronyms are left alone: "Pan" for a PAN card and "Gst" for GST read as
 * mistakes to an Indian operator, because they are.
 */
const ACRONYMS = new Set(['PAN', 'KYC', 'GST', 'CRM', 'OTP', 'ID', 'BHK', 'IUC', 'SBUC', 'TUC']);

/** Proper nouns that are not acronyms and should not be shouted. */
const PROPER: Record<string, string> = { AADHAAR: 'Aadhaar', DIGILOCKER: 'DigiLocker', RAZORPAY: 'Razorpay' };

export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  const words = value.split('_').map((word, i) => {
    const upper = word.toUpperCase();
    if (ACRONYMS.has(upper)) return upper;
    if (PROPER[upper]) return PROPER[upper];
    const lower = word.toLowerCase();
    // Sentence case, not title case — it reads as prose in a table cell.
    return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  });
  return words.join(' ');
}

export function initials(name: string | null | undefined, fallback = '?'): string {
  if (!name?.trim()) return fallback;
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '')).toUpperCase();
}

/** The API masks numbers for ordinary users; admin sees the real one. */
export function mobile(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^\+91(\d{5})(\d{5})$/.exec(value);
  return m ? `+91 ${m[1]} ${m[2]}` : value;
}

export function pct(value: number, total: number): number {
  return total === 0 ? 0 : Math.round((value / total) * 100);
}
